import React, { useState, useRef } from 'react';
import FileDropZone from '../shared/FileDropZone';
import '../CRMProfileScreen.css';
import { useFlashMessage } from '../FlashMessage/FlashMessageContext';
import DocumentNameSuggestionForm from '../DocumentNaming/DocumentNameSuggestionForm';

const HAMSTIRUploadModal = ({ isOpen, onClose, onDocumentSelect, uploading, profileId, partnerName }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [namingPayload, setNamingPayload] = useState(null);
    const fileInputRef = useRef(null);
    const { showError } = useFlashMessage();

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const acceptDroppedFile = (file) => {
        setSelectedFile(file);
    };

    const handleUpload = () => {
        if (!selectedFile) {
            return;
        }
        
        onDocumentSelect('Partner Information', selectedFile, namingPayload);
        
        // Reset form
        setSelectedFile(null);
        setNamingPayload(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        
        onClose();
    };

    const handleCancel = () => {
        setSelectedFile(null);
        setNamingPayload(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onClose();
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (!isOpen) return null;

    const uploadDisabled = !selectedFile || uploading || !namingPayload?.isValid;

    return (
        <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        }}>
            <div className="modal-content" style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '2rem',
                minWidth: '500px',
                maxWidth: '600px',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
            }}>
                <div className="modal-header" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    paddingBottom: '1rem',
                    borderBottom: '1px solid #e5e7eb'
                }}>
                    <h3 style={{ 
                        margin: 0, 
                        color: '#4a6b25', 
                        fontSize: '1.25rem', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        Upload Partner Information Document
                    </h3>
                    <button
                        onClick={handleCancel}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: '#6b7280',
                            padding: '0.25rem'
                        }}
                    >
                        ×
                    </button>
                </div>

                <div className="modal-body">
                    {/* File Upload Section */}
                    <div className="crm-input-group" style={{ marginBottom: '1.5rem' }}>
                        <label className="crm-input-label required">Select File</label>
                        
                        <FileDropZone
                            className="file-drop-zone"
                            disabled={uploading}
                            onFiles={(files) => acceptDroppedFile(files[0])}
                            onError={showError}
                            style={{
                                border: '2px dashed #d1d5db',
                                borderRadius: '8px',
                                padding: '2rem',
                                textAlign: 'center',
                                backgroundColor: '#f9fafb',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer'
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                onChange={handleFileSelect}
                                accept=".pdf,.docx,.xlsx,.pptx,.md,.html,.csv,.png,.jpg,.jpeg,.tiff,.bmp,.webp,.txt,.eml,.msg,.asc,.asciidoc"
                                style={{ display: 'none' }}
                                disabled={uploading}
                            />
                            
                            {selectedFile ? (
                                <div style={{ color: '#4a6b25' }}>
                                    <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                                        {selectedFile.name}
                                    </div>
                                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                        {formatFileSize(selectedFile.size)}
                                    </div>
                                </div>
                            ) : (
                                <div style={{ color: '#6b7280' }}>
                                    <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                                        Drop files here or click to browse
                                    </div>
                                    <div style={{ fontSize: '0.875rem' }}>
                                        Supports: PDF, DOCX, XLSX, PPTX, Markdown (.md), HTML, CSV, Images (PNG, JPG, JPEG, TIFF, BMP, WEBP), TXT, EML, MSG, AsciiDoc (Max 30MB)
                                    </div>
                                </div>
                            )}
                        </FileDropZone>
                    </div>

                    {/* File Information */}
                    {selectedFile && (
                        <div style={{
                            padding: '1rem',
                            backgroundColor: '#f0f9ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '8px',
                            marginBottom: '1.5rem'
                        }}>
                            <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>
                                <strong>File Details:</strong>
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#374151', marginTop: '0.5rem' }}>
                                <div><strong>Name:</strong> {selectedFile.name}</div>
                                <div><strong>Size:</strong> {formatFileSize(selectedFile.size)}</div>
                                <div><strong>Type:</strong> {selectedFile.type || 'Unknown'}</div>
                                <div><strong>Last Modified:</strong> {new Date(selectedFile.lastModified).toLocaleDateString()}</div>
                            </div>
                        </div>
                    )}

                    {selectedFile && (
                        <DocumentNameSuggestionForm
                            autoRun
                            file={selectedFile}
                            module="partner_info"
                            relatedEntityId={profileId || ''}
                            partnerName={partnerName || ''}
                            onChange={setNamingPayload}
                            disabled={uploading}
                        />
                    )}

                    {/* Upload Guidelines */}
                    <div style={{
                        padding: '1rem',
                        backgroundColor: '#fffbeb',
                        border: '1px solid #f59e0b',
                        borderRadius: '8px',
                        marginBottom: '1.5rem'
                    }}>
                        <div style={{ fontSize: '0.875rem', color: '#d97706' }}>
                            <strong>Upload Guidelines:</strong>
                        </div>
                        <ul style={{ fontSize: '0.875rem', color: '#92400e', marginTop: '0.5rem', paddingLeft: '1.25rem' }}>
                            <li>Choose the most appropriate document type for better organization</li>
                            <li>Maximum file size: 30MB</li>
                            <li>Supported formats: PDF, DOC, DOCX, TXT, JPG, PNG, GIF</li>
                            <li>Documents will be available for HAMSTIR profile building</li>
                        </ul>
                    </div>
                </div>

                <div className="modal-footer" style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid #e5e7eb'
                }}>
                    <button
                        onClick={handleCancel}
                        disabled={uploading}
                        style={{
                            background: 'transparent',
                            border: '1px solid #d1d5db',
                            color: '#374151',
                            borderRadius: '8px',
                            padding: '0.75rem 1.5rem',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            opacity: uploading ? 0.6 : 1,
                            transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleUpload}
                        disabled={uploadDisabled}
                        title={
                            selectedFile && !uploading && !namingPayload?.isValid
                                ? 'Add a document name before uploading'
                                : undefined
                        }
                        style={{
                            background: uploadDisabled
                                ? '#6c757d'
                                : 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.75rem 1.5rem',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            cursor: uploadDisabled ? 'not-allowed' : 'pointer',
                            opacity: uploadDisabled ? 0.6 : 1,
                            transition: 'all 0.2s',
                            boxShadow: uploadDisabled
                                ? 'none'
                                : '0 2px 4px rgba(74, 107, 37, 0.25)'
                        }}
                    >
                        {uploading ? 'Uploading...' : 'Upload Document'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HAMSTIRUploadModal; 